import type { ItemDisplayColumn } from '@/api/hanly-v2/hanlyAPIV2.schemas';
import { ProductThumbnail } from '@/components/product-thumbnail';
import { useShowPrices } from '@/contexts/auth';
import { formatYMDSlash } from '@/utils/date';
import { formatItemBreakdown, formatItemMeta, formatYen, toItemColumnVisibility } from './item-display';
import type { OrderItemDisplayData } from './types';

interface OrderLinesViewProps {
  items: OrderItemDisplayData[];
  itemDisplayColumns?: readonly ItemDisplayColumn[];
}

const formatQuantity = (item: OrderItemDisplayData) =>
  `${item.quantity.toLocaleString()}${item.unitLabel ?? ''}`;

/**
 * 数量が入っている明細だけを読み取り専用で並べる(確認画面・発注詳細)。
 * 表示だけなので PC の表とスマホのリストを CSS で出し分ける。
 */
export const OrderLinesView = ({ items, itemDisplayColumns }: OrderLinesViewProps) => {
  const showPrices = useShowPrices();
  const visibility = toItemColumnVisibility(itemDisplayColumns);
  const lines = items.filter((item) => item.quantity > 0);
  const showDeliveryDate = visibility.deliveryDate && lines.some((item) => item.desiredDeliveryDate);

  if (lines.length === 0) {
    return <p className="py-6 text-center text-neutral-600">数量が入力された商品はありません</p>;
  }

  return (
    <>
      <ul className="divide-y divide-neutral-200 border border-neutral-200 md:hidden">
        {lines.map((item) => {
          const meta = formatItemMeta(item, visibility);
          const breakdown = formatItemBreakdown(item);
          return (
            <li key={item.key} className="flex gap-3 bg-white px-3 py-3">
              <ProductThumbnail url={item.thumbnailUrl} size={48} />
              <div className="flex min-w-0 flex-1 flex-col text-sm leading-snug">
                <span className="font-bold">{item.name}</span>
                {meta && <span className="text-sm text-neutral-600">{meta}</span>}
                {showPrices && (
                  <span className="text-neutral-600">
                    ¥{formatYen(item.unitPrice)} × {formatQuantity(item)}
                  </span>
                )}
                {breakdown && <span className="text-sm text-neutral-600">{breakdown}</span>}
                {showDeliveryDate && item.desiredDeliveryDate && (
                  <span className="text-sm text-neutral-600">
                    希望納期 {formatYMDSlash(item.desiredDeliveryDate)}
                  </span>
                )}
              </div>
              <span className="font-bold whitespace-nowrap">
                {showPrices ? `${formatYen(item.amount)} 円` : formatQuantity(item)}
              </span>
            </li>
          );
        })}
      </ul>
      <div className="hidden overflow-x-auto border border-neutral-200 md:block">
        <table className="w-full border-collapse bg-white text-sm">
          <thead>
            <tr className="bg-neutral-100 text-left text-neutral-600">
              <th className="px-3 py-2 font-normal">商品</th>
              {showPrices && <th className="px-3 py-2 text-right font-normal">単価</th>}
              <th className="px-3 py-2 text-right font-normal">数量</th>
              {showPrices && <th className="px-3 py-2 text-right font-normal">金額</th>}
              {showDeliveryDate && <th className="px-3 py-2 font-normal">希望納期</th>}
            </tr>
          </thead>
          <tbody>
            {lines.map((item) => {
              const meta = formatItemMeta(item, visibility);
              const breakdown = formatItemBreakdown(item);
              return (
                <tr key={item.key} className="border-t border-neutral-200">
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-3">
                      <ProductThumbnail url={item.thumbnailUrl} size={40} />
                      <div className="flex flex-col leading-snug">
                        <span className="font-bold">{item.name}</span>
                        {meta && <span className="text-sm text-neutral-600">{meta}</span>}
                      </div>
                    </div>
                  </td>
                  {showPrices && (
                    <td className="px-3 py-2 text-right whitespace-nowrap">¥{formatYen(item.unitPrice)}</td>
                  )}
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {formatQuantity(item)}
                    {breakdown && <div className="text-sm text-neutral-600">{breakdown}</div>}
                  </td>
                  {showPrices && (
                    <td className="px-3 py-2 text-right font-bold whitespace-nowrap">
                      {formatYen(item.amount)} 円
                    </td>
                  )}
                  {showDeliveryDate && (
                    <td className="px-3 py-2 whitespace-nowrap">
                      {formatYMDSlash(item.desiredDeliveryDate)}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};
